import { useEffect, useState, type JSX } from 'react';
import type { AssetEntry } from '@peacock/sdk-js';

/**
 * Shared presentational thumbnail for an asset (2026-06-11).
 *
 * Renders the real 240×240 `thumb_url` preview for image assets; falls
 * back to a MIME-type label for non-images OR when the thumbnail fails
 * to load (404 / Glide signing drift) so the grid never shows the
 * browser's broken-image icon.
 *
 * Reused by: AssetLibrary grid, AssetPickerModal grid, gallery image
 * strip. Purely presentational — no data fetching, no app knowledge.
 */
export function AssetThumb({
  asset,
  selected = false,
  className = '',
}: {
  asset: Pick<AssetEntry, 'mime' | 'original_filename' | 'thumb_url'>;
  selected?: boolean;
  className?: string;
}): JSX.Element {
  const [broken, setBroken] = useState(false);
  // Reset the broken flag when the source changes — otherwise a same-
  // position instance (gallery strip keys by uuid) that 404'd once would
  // stay stuck in the MIME-label fallback even after the assets query
  // refetches a fresh, re-signed thumb_url. (code-review 2026-06-17.)
  useEffect(() => setBroken(false), [asset.thumb_url]);
  const isImage = asset.mime.startsWith('image/');
  const showImage = isImage && typeof asset.thumb_url === 'string' && asset.thumb_url !== '' && !broken;

  return (
    <div
      className={
        'aspect-square w-full overflow-hidden rounded bg-zinc-100 ' +
        (selected ? 'ring-2 ring-zinc-900 ring-offset-1' : '') +
        (className ? ' ' + className : '')
      }
    >
      {showImage ? (
        <img
          src={asset.thumb_url as string}
          alt={asset.original_filename}
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className="h-full w-full object-cover"
        />
      ) : (
        <div className="grid h-full place-items-center text-zinc-400">
          <span className="font-mono text-xs">
            {isImage ? 'Bild' : (asset.mime.split('/').pop() ?? 'Datei')}
          </span>
        </div>
      )}
    </div>
  );
}
